/**
 * How many *items* a cart holds, as a shopper counts them — the JS twin of
 * snippets/cart-item-count.liquid, for the paths that update the cart bubble
 * from a live `/cart.js` payload rather than from re-rendered section HTML.
 *
 * `cart.item_count` is the sum of the lines' quantities, and a Super
 * Subscriptions & Bundles bundle is not a line: the app adds one real,
 * independently-fulfillable line per component (see snippets/cart-products.liquid
 * for the full contract), so a five-product bundle makes `item_count` say 5 where
 * the cart shows a single "Bundle" row. Here one bundle counts as one item.
 *
 * A bundle is identified the way its cart row is: `_ssBundleGroup` +
 * `_ssBundleBar` name the row, `_ssBundleKey` is one box within it. Lines with a
 * group but no key are the app's add-ons (`_ssBundleAddOn`) — one-time upsells
 * that get their own ordinary row, so they count as ordinary items.
 *
 * @param {{ item_count?: number, items?: Array<{ quantity?: number, properties?: Record<string, string> | null }> } | null | undefined} cart
 * @returns {number}
 */
export function cartDisplayCount(cart) {
  const items = cart?.items;

  // No line detail to group by (a partial payload, or a fetch that failed):
  // the raw count is still better than nothing.
  if (!Array.isArray(items)) return Number(cart?.item_count) || 0;

  /** Bundle units already counted, keyed row-id + key — see the Liquid twin. */
  const countedUnits = new Set();
  let count = 0;

  for (const item of items) {
    const properties = item?.properties;
    const group = properties?.['_ssBundleGroup'];
    const key = properties?.['_ssBundleKey'];

    if (group && key) {
      const unit = `${group}~${properties?.['_ssBundleBar'] ?? '0'}~${key}`;
      if (countedUnits.has(unit)) continue;
      countedUnits.add(unit);
      count += 1;
      continue;
    }

    count += Number(item?.quantity) || 0;
  }

  return count;
}

// `Theme` is declared `const` in snippets/scripts.liquid, which makes it a global
// lexical binding rather than a property of globalThis — so this is a mutation of
// that object, and the only way the theme's classic (non-module) scripts can
// reach this function. Guarded because those scripts are also the ones that would
// break if this module ever loaded before that inline `const` ran; they all fall
// back to `cart.item_count` when the function is absent.
try {
  Theme.cartDisplayCount = cartDisplayCount;
} catch {
  // no-op
}
